import React, { useState, useEffect, Fragment } from 'react';
import * as XLSX from 'xlsx';
import { FaTimes, FaUpload, FaFileExcel, FaSearch, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { DataGrid } from '@mui/x-data-grid';
import { Tab } from '@headlessui/react';
import { supabase } from './supabaseClient';
import { toast } from 'react-toastify';

// AHTOps uploader: ingests both sheets in "AHT Guatemala Ops.xlsx" and loads into mvw_raw.mvw_aht_ops_staging.
const AHTOps = ({ session, onCancel }) => {
  // While we validate the business need, we only UPLOAD the first sheet ("AHT Form").
  // We still READ/PARSE the second sheet ("Scheduling GT") to keep the integration ready,
  // but we intentionally do not insert those rows into the database yet.
  const ENABLE_SCHEDULING_UPLOAD = false;

  const [rows, setRows] = useState([]);
  const [rawJsonData, setRawJsonData] = useState([]); // Datos originales del Excel sin transformar
  const [columns, setColumns] = useState([]);
  const [uploadedRows, setUploadedRows] = useState([]);
  const [uploadedColumns, setUploadedColumns] = useState([]);
  const [fileName, setFileName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dbColumns, setDbColumns] = useState([]);
  const [isSchemaLoading, setIsSchemaLoading] = useState(true);
  const [validationResult, setValidationResult] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [schemaError, setSchemaError] = useState(null);

  const fetchDbColumns = async () => {
    setIsSchemaLoading(true);
    setSchemaError(null);
    try {
      // IMPORTANT: the RPC lives in schema mvw_raw, so we must call it via schema-qualified client.
      // Otherwise Supabase will look in the default (public) schema and fail with:
      // "Could not find the function public.get_aht_staging_columns..."
      const { data, error } = await supabase
        .schema('mvw_raw')
        .rpc('get_aht_staging_columns');
      if (error) {
        throw error;
      }
      // Filtramos columnas de sistema que no se esperan en el Excel
      const systemCols = ["id", "inserted_at", "created_at"]; // batch_id/raw_payload are expected in staging
      const expectedCols = data.map(c => c.column_name).filter(c => !systemCols.includes(c));
      setDbColumns(expectedCols);
    } catch (error) {
      console.error("Error fetching schema:", error);
      setSchemaError(error.message);
      toast.error(`Error al obtener definición de tabla: ${error.message}`);
    } finally {
      setIsSchemaLoading(false);
    }
  };

  const parseDdMmYyyy = (input) => {
    if (!input) return null;
    const s = String(input).trim();
    const m = s.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/);
    if (!m) return null;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    if (!dd || !mm || !yyyy) return null;
    const iso = `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    return iso;
  };

  const parseAgent = (agentStr) => {
    const s = String(agentStr || '').trim();
    if (!s) return { agent_name: null, agent_code: null, agent_display: null };
    const parts = s.split(' - ').map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) {
      return { agent_name: s, agent_code: null, agent_display: s };
    }
    const agent_code = parts[parts.length - 1];
    const agent_name = parts.slice(0, -1).join(' - ');
    return { agent_name, agent_code, agent_display: s };
  };

  // Returns normalized HH:MM:SS string when possible; otherwise null.
  // Supports:
  //  - Total hours: H:MM:SS
  //  - AHT: MM:SS (interpreted as minutes:seconds)
  //
  // DB note:
  // - In Postgres, columns like total_hours/aht are type INTERVAL.
  // - PostgREST/Supabase expects INTERVAL values as *strings* that Postgres can parse.
  // - Formats like '2:04:18' or '02:04:18' are valid interval inputs.
  const normalizeToHms = (value, { assumeMinutesSeconds = false } = {}) => {
    const s = String(value || '').trim();
    if (!s) return null;
    const parts = s.split(':').map(p => p.trim());
    if (parts.some(p => p === '' || !/^\d+$/.test(p))) return null;

    if (parts.length === 3) {
      const [h, m, sec] = parts.map(Number);
      if (m > 59 || sec > 59) return null;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }

    if (parts.length === 2 && assumeMinutesSeconds) {
      const [m, sec] = parts.map(Number);
      if (m > 59 || sec > 59) return null;
      return `00:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    }

    return null;
  };

  const normalizeToTime = (value) => {
    const s = String(value || '').trim();
    if (!s) return null;
    // 24h HH:MM
    const m24 = s.match(/^\s*(\d{1,2}):(\d{2})\s*$/);
    if (m24) {
      const hh = Number(m24[1]);
      const mm = Number(m24[2]);
      if (hh > 23 || mm > 59) return null;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
    }
    // 12h like 9:00 AM
    const m12 = s.match(/^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i);
    if (m12) {
      let hh = Number(m12[1]);
      const mm = Number(m12[2]);
      const ap = m12[3].toUpperCase();
      if (hh < 1 || hh > 12 || mm > 59) return null;
      if (ap === 'PM' && hh !== 12) hh += 12;
      if (ap === 'AM' && hh === 12) hh = 0;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
    }
    return null;
  };

  const parseIntegerLoose = (value) => {
    // Returns { value: integer|null, warning: string|null, raw: string }
    const raw = String(value ?? '').trim();
    if (raw === '') return { value: null, warning: null, raw };

    // normalize comma decimals
    const normalized = raw.replace(',', '.');
    const n = Number(normalized);
    if (!Number.isFinite(n)) {
      return { value: null, warning: `No es numérico: "${raw}"`, raw };
    }
    if (Number.isInteger(n)) {
      return { value: n, warning: null, raw };
    }
    // Some exports may come as decimals (e.g. 7.05). For OLTP integer column we truncate.
    const truncated = Math.trunc(n);
    return {
      value: truncated,
      warning: `Decimal "${raw}" → se truncó a ${truncated}`,
      raw,
    };
  };

  const buildCanonicalRowsFromWorkbook = (wb) => {
    const outRows = [];
    const outRaw = [];
    const parseWarnings = [];

    // Sheet 1: AHT Form
    const ahtSheetName = wb.SheetNames.find(n => String(n).trim() === 'AHT Form') || wb.SheetNames[0];
    const wsAht = wb.Sheets[ahtSheetName];
    if (wsAht) {
      // Read header row (row 1) to extract report date.
      const aoa = XLSX.utils.sheet_to_json(wsAht, { header: 1, raw: false, defval: '' });
      const headerRow = aoa[0] || [];
      // Find first DD/MM/YYYY cell.
      const reportDateIso = headerRow.map(v => parseDdMmYyyy(v)).find(Boolean) || null;

      // Data rows as objects (XLSX will create keys like 25/02/2026 and 25/02/2026_1)
      const jsonData = XLSX.utils.sheet_to_json(wsAht, { defval: '', raw: false });
      const dateLabel = headerRow.map(v => String(v || '').trim()).find(v => parseDdMmYyyy(v));
      const dateKey0 = dateLabel || null;
      const dateKey1 = dateKey0 ? `${dateKey0}_1` : null;

      jsonData.forEach((r, i) => {
        const rawRow = r;
        const agentCell = rawRow['Agent Daily Statistics - Total Hours'] || rawRow['Agent Daily AHT '] || rawRow['Agent Daily Statistics - Calls Completes'] || '';
        const { agent_name, agent_code } = parseAgent(agentCell);

        const totalHoursStr = dateKey0 && rawRow[dateKey0] ? rawRow[dateKey0] : '';
        const callsCompletedStr = dateKey1 && rawRow[dateKey1] ? rawRow[dateKey1] : '';
        const ahtStr = rawRow['AHT'] || '';
        const shiftStartStr = rawRow['__EMPTY_3'] || '';

        const total_hours = normalizeToHms(totalHoursStr, { assumeMinutesSeconds: false });
        const aht = normalizeToHms(ahtStr, { assumeMinutesSeconds: true });
        const callsParsed = parseIntegerLoose(callsCompletedStr);
        const calls_completed = callsParsed.value;
        if (callsParsed.warning) {
          parseWarnings.push({
            row_number: i + 2,
            status: 'warning',
            excelCol: `Fila ${i + 2} (AHT Form)`,
            dbCol: 'calls_completed',
            message: callsParsed.warning,
            raw: callsParsed.raw,
          });
        }
        const shift_start = normalizeToTime(shiftStartStr);

        outRaw.push({ sheet: ahtSheetName, row_number: i + 2, ...rawRow });
        outRows.push({
          id: `AHT-${i}`,
          batch_id: null, // assigned at upload time
          source_sheet: 'AHT Form',
          row_number: i + 2,
          report_date: reportDateIso,
          agent_name,
          agent_code,
          total_hours,
          calls_completed: Number.isFinite(calls_completed) ? calls_completed : null,
          aht,
          shift_start,
          full_name: null,
          lob: null,
          break_time: null,
          lunch_time: null,
          raw_payload: rawRow,
        });
      });
    }

    // Sheet 2: Scheduling GT
    // NOTE: Parsed but not uploaded unless ENABLE_SCHEDULING_UPLOAD is turned on.
    const schedSheetName = wb.SheetNames.find(n => String(n).trim().toLowerCase() === 'scheduling gt')
      || wb.SheetNames.find(n => String(n).trim().toLowerCase() === 'scheduling gt')
      || wb.SheetNames.find(n => String(n).toLowerCase().includes('scheduling'));
    const wsSched = schedSheetName ? wb.Sheets[schedSheetName] : null;
    if (wsSched) {
      const jsonData = XLSX.utils.sheet_to_json(wsSched, { defval: '', raw: false });
      jsonData.forEach((r, i) => {
        const rawRow = r;
        const full_name = rawRow['Full Name'] ? String(rawRow['Full Name']).trim() : null;
        const lob = rawRow['LOB'] ? String(rawRow['LOB']).trim() : null;
        const break_time = normalizeToTime(rawRow['Break']);
        const lunch_time = normalizeToTime(rawRow['Lunch']);

        outRaw.push({ sheet: schedSheetName, row_number: i + 2, ...rawRow });
        outRows.push({
          id: `SCH-${i}`,
          batch_id: null,
          source_sheet: 'Scheduling GT',
          row_number: i + 2,
          report_date: null,
          agent_name: null,
          agent_code: null,
          total_hours: null,
          calls_completed: null,
          aht: null,
          shift_start: null,
          full_name,
          lob,
          break_time,
          lunch_time,
          raw_payload: rawRow,
        });
      });
    }

    return { outRows, outRaw, parseWarnings };
  };

  const hasValidationErrors = validationResult.some(item => item.status === 'error');
  // El botón Cargar solo se habilita si hay validación ejecutada y no hay errores
  const canUpload = validationResult.length > 0 && !hasValidationErrors;

  // Obtener columnas esperadas de la BD al montar el componente
  useEffect(() => {
    fetchDbColumns();
  }, []);

  const handleFileChange = (e) => {
    setUploadError(null); // Limpiar errores previos al cargar nuevo archivo
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (dbColumns.length === 0) {
        toast.warn("El esquema de la base de datos no está cargado. La validación puede ser incorrecta. Intente de nuevo.");
        // Reset file input so user can try again
        e.target.value = null;
        setFileName('');
        return;
      }

      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });

      const { outRows, outRaw, parseWarnings } = buildCanonicalRowsFromWorkbook(wb);

      // Focus on sheet 1 (AHT Form) for uploads.
      const ahtOnlyRows = outRows.filter(r => r.source_sheet === 'AHT Form');
      const schedulingRows = outRows.filter(r => r.source_sheet === 'Scheduling GT');

      // Some AHT Form exports include trailing/summary rows (totals) that don't have an agent.
      // Those rows are not useful for row-level OLTP loads, so we ignore them.
      const ignoredAhtRows = ahtOnlyRows.filter(r => !r.agent_name || !r.agent_code);
      const cleanAhtRows = ahtOnlyRows.filter(r => r.agent_name && r.agent_code);

      if (ignoredAhtRows.length > 0) {
        toast.warn(
          `AHT Form: se omitieron ${ignoredAhtRows.length} filas sin agente (probables totales/resumen). ` +
          `Se cargarán ${cleanAhtRows.length} filas válidas.`
        );
        console.info('[AHTOps] Ignored AHT rows without agent_code/agent_name:', ignoredAhtRows);
      }

      // Keep raw JSON for AHT Form only in the UI to avoid confusion while we validate Scheduling GT.
      const rawAhtOnly = outRaw.filter(r => String(r.sheet || '').trim() === 'AHT Form');

      if (!ENABLE_SCHEDULING_UPLOAD && schedulingRows.length > 0) {
        // Informational only (does not block upload).
        toast.info(`Scheduling GT detectado (${schedulingRows.length} filas). Por el momento no se sube; solo se está leyendo para validación.`);
        console.info(`[AHTOps] Scheduling GT parsed (${schedulingRows.length} rows) but preview/upload is focused on AHT Form.`);
      }

      // Build a stable set of canonical fields for preview (exclude huge raw_payload by default)
      const previewFields = [
        'source_sheet',
        'row_number',
        'report_date',
        'agent_name',
        'agent_code',
        'total_hours',
        'calls_completed',
        'aht',
        'shift_start',
        'full_name',
        'lob',
        'break_time',
        'lunch_time',
      ];
      const cols = previewFields.map((field) => ({
        field,
        headerName: field,
        flex: 1,
        minWidth: 150,
      }));
      setColumns(cols);
      // Preview + Upload: only AHT Form rows.
      // Scheduling GT remains parsed in code (and visible in console) but is not shown/uploaded.
      setRows(cleanAhtRows);
      setRawJsonData(rawAhtOnly);

      // Validation: ensure the DB contains all columns we will send (excluding id)
      // NOTE: DB may have additional columns; that's fine.
      const intendedUploadCols = [
        'batch_id',
        'source_sheet',
        'row_number',
        'report_date',
        'agent_name',
        'agent_code',
        'total_hours',
        'calls_completed',
        'aht',
        'shift_start',
        'full_name',
        'lob',
        'break_time',
        'lunch_time',
        'raw_payload',
      ];
      const dbSet = new Set(dbColumns);
      const missingInDb = intendedUploadCols.filter(c => !dbSet.has(c));
      const schemaChecks = missingInDb.map((c) => ({
        excelCol: '(App → Staging)',
        dbCol: c,
        status: 'error',
      }));

      // Data checks (validate only AHT Form because Scheduling is not uploaded yet)
      const dataErrors = [];
      const ahtRows = cleanAhtRows;
      const badDate = ahtRows.filter(r => !r.report_date);
      if (badDate.length > 0) {
        dataErrors.push({
          excelCol: `Datos AHT Form: ${badDate.length} filas sin report_date (header DD/MM/YYYY)`,
          dbCol: 'report_date',
          status: 'error',
        });
      }
      const badTotal = ahtRows.filter(r => r.total_hours === null);
      if (badTotal.length > 0) {
        dataErrors.push({
          excelCol: `Datos AHT Form: ${badTotal.length} filas con total_hours inválido (H:MM:SS)`,
          dbCol: 'total_hours',
          status: 'warning',
        });
      }
      const badAht = ahtRows.filter(r => r.aht === null);
      if (badAht.length > 0) {
        dataErrors.push({
          excelCol: `Datos AHT Form: ${badAht.length} filas con aht inválido (MM:SS)`,
          dbCol: 'aht',
          status: 'warning',
        });
      }

      // Warnings found while parsing individual rows (e.g. calls_completed as decimal)
      const rowParseWarnings = (parseWarnings || []).map((w) => ({
        excelCol: w.excelCol,
        dbCol: `${w.dbCol}: ${w.message}`,
        status: w.status,
        tooltip: `Valor original: ${w.raw}`,
      }));

      const validation = [...schemaChecks, ...dataErrors, ...rowParseWarnings];
      if (validation.length === 0) {
        validation.push({
          excelCol: 'Validación',
          dbCol: 'OK',
          status: 'ok',
        });
      }
      // Sort: errors first, then warnings, then ok
      validation.sort((a, b) => {
        const statusOrder = { error: 0, warning: 1, ok: 2 };
        return (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      });
      setValidationResult(validation);
    };
    reader.readAsBinaryString(file);
  };

  const handleCancel = () => {
    setRows([]);
    setRawJsonData([]);
    setColumns([]);
    setUploadedRows([]);
    setUploadedColumns([]);
    setFileName('');
    setSearchTerm('');
    setUploadError(null);
    setIsUploaded(false);
    setSelectedIndex(0);
    setValidationResult([]);
    setBatchId(null);
    // Llama a la función onCancel pasada por props para volver a la pantalla principal
    if (onCancel) {
      onCancel();
    }
  };

  const handleUpload = async () => {
    try {
      // If the button looks like it does nothing, it's often because it's disabled by `canUpload`.
      // We also log and show toasts here to make the reason explicit.
      console.info('[AHTOps] handleUpload click', {
        fileName,
        rowsInMemory: rows.length,
        validationItems: validationResult.length,
        canUpload,
        isUploading,
        isUploaded,
      });

      if (!fileName) {
        const msg = 'Seleccione un archivo Excel antes de cargar.';
        console.warn('[AHTOps] upload blocked:', msg);
        toast.error(msg);
        return;
      }

      if (validationResult.length === 0) {
        const msg = 'No hay validación disponible. Re-cargue el archivo o reintente cargar el esquema.';
        console.warn('[AHTOps] upload blocked:', msg);
        toast.error(msg);
        return;
      }

      if (hasValidationErrors) {
        const msg = 'No se puede cargar: la validación tiene errores. Revise la pestaña de Validación.';
        console.warn('[AHTOps] upload blocked:', msg, validationResult);
        toast.error(msg);
        return;
      }

      setIsUploading(true);
      setUploadError(null);

      // Upload scope control:
      // - For now, only upload "AHT Form" rows.
      // - Keep parsing/previewing Scheduling GT rows while we validate the need.
      const rowsToUpload = ENABLE_SCHEDULING_UPLOAD
        ? rows
        : rows.filter((r) => r.source_sheet === 'AHT Form');

      if (rowsToUpload.length === 0) {
        const msg = 'No hay registros para cargar (AHT Form). Verifique que el Excel tenga datos.';
        console.warn('[AHTOps] upload blocked:', msg);
        toast.error(msg);
        return;
      }

      if (!ENABLE_SCHEDULING_UPLOAD) {
        const schedCount = rows.filter((r) => r.source_sheet === 'Scheduling GT').length;
        if (schedCount > 0) {
          console.info(`[AHTOps] Scheduling GT rows detected (${schedCount}) but not uploaded (ENABLE_SCHEDULING_UPLOAD=false).`);
        }
      }

      // 1. Crear el registro maestro en mvw_call_upload_batches
      const insertPayload = {
        file_name: fileName,
        uploaded_by: session.user.email,
        records_processed: rowsToUpload.length
      };
       
      console.log("Intentando insertar en mvw_raw.mvw_call_upload_batches. Payload:", insertPayload);
      toast.info('Creando lote (batch)...');

      const { data: batchData, error: batchError } = await supabase
        .schema('mvw_raw')
        .from('mvw_call_upload_batches')
        .insert(insertPayload)
        .select()
        .single();

      if (batchError) {
        console.error("Error DETALLADO de Supabase al insertar lote:", batchError);
        throw new Error(`Error al crear lote: ${batchError.message} (Código: ${batchError.code || 'Desconocido'}) - Revisa la consola (F12) para más detalles.`);
      }
      
      const newBatchId = batchData.id;
      setBatchId(newBatchId);

      // Make the batch_id visible immediately in the UI (grid + JSON tab)
      // so the operator can correlate the file/rows with the OLTP batch before/while inserting.
      setRows((prev) => prev.map((r) => ({ ...r, batch_id: newBatchId })));
      setRawJsonData((prev) => prev.map((r) => ({ ...r, batch_id: newBatchId })));

      // 2. Preparamos los registros para subir
      const recordsToUpload = rowsToUpload.map(({ id, batch_id, ...rest }) => ({
        ...rest,
        batch_id: newBatchId,
        // Ensure raw_payload is JSONB serializable (it is plain object)
        // and embed batch metadata for traceability (useful for OLTP auditing).
        raw_payload: {
          ...(rest.raw_payload || {}),
          __batch_id: newBatchId,
          __source_sheet: rest.source_sheet,
          __row_number: rest.row_number,
        },
      }));

      // 3. Insertar registros directamente desde React (en lotes)
      // Esto es más robusto que la Edge Function para cargas directas y evita problemas de CORS
      const CHUNK_SIZE = 1000; // Insertamos de 1000 en 1000
      toast.info(`Insertando ${recordsToUpload.length} registros en staging...`);
      
      for (let i = 0; i < recordsToUpload.length; i += CHUNK_SIZE) {
        const chunk = recordsToUpload.slice(i, i + CHUNK_SIZE);

        const { error: insertError } = await supabase
          .schema('mvw_raw')
          .from('mvw_aht_ops_staging')
          .insert(chunk);

        if (insertError) {
          console.error("Error insertando lote:", insertError);
          throw new Error(`Error al insertar registros (lote ${Math.floor(i/CHUNK_SIZE) + 1}): ${insertError.message}`);
        }
      }

      // 4. Verificar carga consultando la base de datos
      
      const { data: dbRows, error: dbError, count: insertedCount } = await supabase
        .schema('mvw_raw')
        .from('mvw_aht_ops_staging')
        .select('*', { count: 'exact', head: false })
        .eq('batch_id', newBatchId);

      if (dbError) {
        throw new Error(`Carga realizada, pero error al verificar registros: ${dbError.message}`);
      }

      // Nota: la tabla mvw_call_upload_batches que nos diste no incluye records_inserted.
      // Por compatibilidad, no hacemos UPDATE de conteo final aquí.

      if (dbRows && dbRows.length > 0) {
        // Generar columnas dinámicas para la vista de base de datos
        const firstRow = dbRows[0];
        const cols = Object.keys(firstRow).map((key) => ({
          field: key,
          headerName: key,
          flex: 1,
          minWidth: 150,
        }));
        setUploadedColumns(cols);
        setUploadedRows(dbRows);
      }

      setIsUploaded(true);
      setSelectedIndex(2); // Cambiar automáticamente a la tercera pestaña
      toast.success(`¡Éxito! Lote ${newBatchId} cargado con ${insertedCount} de ${rowsToUpload.length} registros.`);

    } catch (err) {
      console.error("Error en handleUpload:", err);
      const msg = err.message || "Ocurrió un error desconocido durante la carga.";
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  // Filtramos las filas para el buscador global (Vista Previa)
  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Filtramos las filas para el buscador global (Vista Base de Datos)
  const filteredUploadedRows = uploadedRows.filter((row) => {
    if (!searchTerm) return true;
    return Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="form-card">
      <h2>Cargar AHT Ops (Guatemala)</h2>
      <p className="form-description">
        Cargue el Excel con las hojas <b>AHT Form</b> y <b>Scheduling GT</b>. El sistema normaliza y valida antes de insertar.
      </p>

      {!fileName && (
        <>
          {schemaError ? (
            <div style={{ textAlign: 'center', color: '#721c24', marginBottom: '1rem' }}>
              <p>Error cargando esquema: {schemaError}</p>
              <button className="btn-primary" onClick={fetchDbColumns} style={{ maxWidth: '150px' }}>
                Reintentar
              </button>
            </div>
          ) : (
            <label htmlFor="file-upload" className={`btn-primary file-upload-label ${isSchemaLoading ? 'disabled' : ''}`}>
              {isSchemaLoading ? (
                <>
                  <FaSpinner className="spinner" style={{ marginRight: '8px' }} />
                  Cargando esquema...
                </>
              ) : (
                <>
                  <FaFileExcel style={{ marginRight: '8px' }} />
                  Seleccionar Archivo Excel
                </>
              )}
            </label>
          )}
        </>
      )}
      <input id="file-upload" type="file" onChange={handleFileChange} accept=".xlsx, .xls" disabled={isSchemaLoading || !!schemaError} />

      {fileName && (
        <>
          <div className="file-info-card">
            <div className="info-item">
              <span className="info-label">Archivo</span>
              <span className="info-value">{fileName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Registros</span>
              <span className="info-value">{rows.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Estado</span>
              <span className="info-value">
                {isUploaded ? (
                   <span style={{ color: 'green', display: 'flex', alignItems: 'center', gap: '5px' }}><FaCheckCircle /> Cargado</span>
                ) : isUploading ? (
                   <span style={{ color: '#007bff', display: 'flex', alignItems: 'center', gap: '5px' }}><FaSpinner className="spinner"/> Procesando</span>
                ) : (
                   <span style={{ color: '#6c757d' }}>Pendiente</span>
                )}
              </span>
            </div>
            {batchId && (
              <div className="info-item">
                <span className="info-label">Batch ID</span>
                <span className="info-value highlight">#{batchId}</span>
              </div>
            )}
          </div>

          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            <div className="tabs-header">
              <Tab.List className="tab-list">
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Vista Tabla
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Vista JSON
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`}>
                      Validación de Columnas
                    </button>
                  )}
                </Tab>
                <Tab as={Fragment}>
                  {({ selected }) => (
                    <button className={`tab-btn ${selected ? 'selected' : ''}`} disabled={!isUploaded && uploadedRows.length === 0}>
                      Registros Cargados {batchId ? `(Lote #${batchId})` : '(BD)'}
                    </button>
                  )}
                </Tab>
              </Tab.List>

              <div className="action-buttons">
                <button onClick={handleCancel} className="btn-cancel">
                  <FaTimes style={{ marginRight: '8px' }} />
                  Cancelar
                </button>
                {/*
                  NOTE:
                  Previously this button was disabled when !canUpload. A disabled <button> does not fire onClick,
                  which looks like "it does nothing". We keep the validation gate in handleUpload() (with toasts/logs)
                  and only disable for active upload or after success.
                */}
                <button onClick={handleUpload} className="btn-confirm" disabled={isUploading || isUploaded}>
                  {isUploading ? (
                    <>
                      <FaSpinner className="spinner" style={{ marginRight: '8px' }} />
                      Cargando...
                    </>
                  ) : (
                    <>
                      <FaUpload style={{ marginRight: '8px' }} />
                      {isUploaded ? 'Cargado' : 'Cargar'}
                    </>
                  )}
                </button>
                {!canUpload && !isUploading && !isUploaded && (
                  <div style={{ fontSize: '0.8rem', color: '#b45309', maxWidth: '320px' }}>
                    Botón habilitado para diagnóstico. Revise la pestaña <b>Validación de Columnas</b> antes de cargar.
                  </div>
                )}
              </div>
            </div>

            <Tab.Panels>
              <Tab.Panel>
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input 
                      type="text" 
                      placeholder="Buscar..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>
                <div className="grid-container">
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    density="compact" // Para el look minimalista
                    sx={{ border: 0 }}
                  />
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="json-viewer">
                  {/* Mostrar los datos originales del Excel tal como vienen del archivo */}
                  <pre>{JSON.stringify(rawJsonData, null, 2)}</pre>
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="grid-container" style={{ overflowY: 'auto', padding: '1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>Columna Excel</th>
                        <th style={{ padding: '8px' }}>Columna Base de Datos (Esperada)</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.map((item, idx) => (
                        <tr key={idx} style={{ 
                            borderBottom: '1px solid #f0f0f0', 
                            backgroundColor: item.status === 'error' ? '#fff5f5' : (item.status === 'warning' ? '#fffbeb' : 'transparent') 
                          }}>
                           <td
                             style={{ padding: '8px', fontWeight: item.status !== 'ok' ? 'bold' : 'normal' }}
                             title={item.tooltip || ''}
                           >
                             {item.excelCol}
                           </td>
                           <td style={{ padding: '8px' }} title={item.tooltip || ''}>{item.dbCol}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {item.status === 'ok' && (
                              <span style={{ color: 'green', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                <FaCheckCircle /> OK
                              </span>
                            )}
                             {item.status === 'warning' && (
                               <span style={{ color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                 <FaExclamationCircle /> Warning
                               </span>
                             )}
                             {item.status === 'error' && (
                               <span style={{ color: 'red', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                 <FaExclamationCircle /> Error
                               </span>
                             )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input 
                      type="text" 
                      placeholder="Buscar en BD..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>
                <div className="grid-container">
                  <DataGrid
                    rows={filteredUploadedRows}
                    columns={uploadedColumns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    density="compact"
                    sx={{ border: 0 }}
                  />
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          {uploadError && <p className="error-message" style={{ marginTop: '1rem' }}>{uploadError}</p>}
        </>
      )}
    </div>
  );
};

export default AHTOps;
